import {
  ProductLineSummaryData,
  ProductLineSummaryItem,
  ProductLineSummaryTreeNode,
} from 'ts/interfaces/productLineSummary.interface'
import type { ApprovalDetail } from 'ts/interfaces/productsDetails.interface'
import { getCompositeIds, parseDealCubeHistoryByLevel } from '../../utils/parseDealCubeHistory'

interface DealCubeProductLineDetail {
  productLineCode: string
  estimatedDiscountPercentage: number
  netMarginPercentage: number
}

interface DealCubeHistoryDetail {
  level: string | number
  approversByLevel?: Array<{ decisionReasonCode: string | null }>
  productlineDetails: DealCubeProductLineDetail[]
}

interface ProductLineTotalsInput {
  totalNetRevenue?: number | null
  totalDiscountPercentage?: number | null
  totalOGMPercentage?: number | null
}

const normalizeOgmPercent = (value: number | null | undefined): number => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return 0
  }

  return Number(value.toFixed(2))
}

const getOgmFields = (
  authNetMarginPercentageUpdated: number | null | undefined,
  authNetMarginPercentage: number | null | undefined,
  updatedCm: number | null | undefined,
  cm: number | null | undefined,
  netMarginPercentage: number | null | undefined
): Pick<ProductLineSummaryTreeNode, 'ogmPercent' | 'requestedOgmPercent'> => ({
  ogmPercent: normalizeOgmPercent(
    authNetMarginPercentageUpdated ?? updatedCm ?? cm ?? netMarginPercentage
  ),
  requestedOgmPercent: normalizeOgmPercent(authNetMarginPercentage ?? cm ?? netMarginPercentage),
})

const getTotalDiscountFields = (
  totalDiscountAuthPercentageUpdated: number | null | undefined,
  totalDiscountAuthPercentage: number | null | undefined,
  estimatedDiscountPercentageUpdated: number | null | undefined,
  estimatedDiscountPercentage: number | null | undefined
): Pick<ProductLineSummaryTreeNode, 'totalDiscountPercent' | 'totalDiscountPercentUpdated'> => ({
  totalDiscountPercent: totalDiscountAuthPercentage ?? estimatedDiscountPercentage ?? 0,
  totalDiscountPercentUpdated:
    totalDiscountAuthPercentageUpdated ?? estimatedDiscountPercentageUpdated ?? null,
})

const buildCategoryNode = (
  categoryItem: ProductLineSummaryItem | undefined,
  psgCode: string,
  categoryCd: string,
  categoryName: string
): ProductLineSummaryTreeNode => ({
  id: `cat-${psgCode}-${categoryCd}`,
  hierarchyPath: [psgCode, categoryName],
  hierarchyCode: categoryName,
  description: categoryName,
  netRevenue: categoryItem?.estimatedPnBdNetRevenue ?? 0,
  dataPoints: undefined,
  ...getTotalDiscountFields(
    categoryItem?.totalDiscountAuthPercentageUpdated,
    categoryItem?.totalDiscountAuthPercentage,
    categoryItem?.estimatedDiscountPercentageUpdated,
    categoryItem?.estimatedDiscountPercentage
  ),
  ...getOgmFields(
    categoryItem?.authNetMarginPercentageUpdated,
    categoryItem?.authNetMarginPercentage,
    categoryItem?.updatedCm,
    categoryItem?.cm,
    categoryItem?.netMarginPercentage
  ),
  updatedCm: categoryItem?.updatedCm ?? null,
  cm: categoryItem?.cm ?? null,
  expertDiscountPercentage: categoryItem?.expertDiscountPercentage ?? undefined,
  floorDiscountPercentage: categoryItem?.floorDiscountPercentage ?? undefined,
  typicalDiscountPercentage: categoryItem?.typicalDiscountPercentage ?? undefined,
  level: 1,
})

const buildProductLineNode = (
  item: ProductLineSummaryItem,
  psgCode: string,
  categoryCd: string,
  categoryName: string,
  approvalData: Record<string, ApprovalDetail>
): ProductLineSummaryTreeNode => ({
  id: `pl-${psgCode}-${categoryCd}-${item.productLineCode}`,
  hierarchyPath: [psgCode, categoryName, item.productLineCode],
  hierarchyCode: item.productLineCode,
  description: item.plDescription ?? '',
  netRevenue: item.estimatedPnBdNetRevenue ?? 0,
  dataPoints: item.dataPoints,
  ...getTotalDiscountFields(
    item.totalDiscountAuthPercentageUpdated,
    item.totalDiscountAuthPercentage,
    item.estimatedDiscountPercentageUpdated,
    item.estimatedDiscountPercentage
  ),
  ...getOgmFields(
    item.authNetMarginPercentageUpdated,
    item.authNetMarginPercentage,
    item.updatedCm,
    item.cm,
    item.netMarginPercentage
  ),
  updatedCm: item.updatedCm ?? null,
  cm: item.cm ?? null,
  expertDiscountPercentage: item.expertDiscountPercentage ?? undefined,
  floorDiscountPercentage: item.floorDiscountPercentage ?? undefined,
  typicalDiscountPercentage: item.typicalDiscountPercentage ?? undefined,
  approvalData,
  level: 2,
})

export const transformProductLineSummaryData = (
  apiData: ProductLineSummaryItem[],
  dealCubeHistoryDetails: DealCubeHistoryDetail[] = [],
  totals?: ProductLineTotalsInput
): ProductLineSummaryData => {
  const emptyResult: ProductLineSummaryData = {
    items: [],
    totals: {
      netRevenue: totals?.totalNetRevenue ?? 0,
      totalDiscountPercent: totals?.totalDiscountPercentage ?? 0,
      ogmPercent: totals?.totalOGMPercentage ?? 0,
    },
  }

  if (!apiData?.length) return emptyResult

  // Single pass: build category lookup map and nested psg → category groups simultaneously
  const categoryItemsMap = new Map<string, ProductLineSummaryItem>()
  const psgGroups = new Map<string, Map<string, ProductLineSummaryItem[]>>()

  for (const item of apiData) {
    if (item.busGroupCode === '') {
      if (item.plCategoryCd) categoryItemsMap.set(item.plCategoryCd, item)
      continue
    }

    const { busGroupCode, plCategoryCd } = item
    const psgCode = busGroupCode ?? ''
    const categoryCd = plCategoryCd ?? ''

    if (!psgGroups.has(psgCode)) psgGroups.set(psgCode, new Map())
    const categoryGroups = psgGroups.get(psgCode)!

    if (!categoryGroups.has(categoryCd)) categoryGroups.set(categoryCd, [])
    categoryGroups.get(categoryCd)!.push(item)
  }

  const historyByApproval = parseDealCubeHistoryByLevel(
    dealCubeHistoryDetails,
    getCompositeIds,
    (detail) => detail.productlineDetails,
    (lineDetail) => lineDetail.productLineCode
  )

  const treeNodes: ProductLineSummaryTreeNode[] = []

  psgGroups.forEach((categoryGroups, psgCode) => {
    treeNodes.push({
      id: `psg-${psgCode}`,
      hierarchyPath: [psgCode],
      hierarchyCode: psgCode,
      description: psgCode,
      level: 0,
    })

    categoryGroups.forEach((categoryItems, categoryCd) => {
      const categoryItem = categoryItemsMap.get(categoryCd)
      const categoryName = categoryItem?.productLineCode || categoryCd

      // Push cat node first, then pl nodes directly — no intermediate array needed
      treeNodes.push(buildCategoryNode(categoryItem, psgCode, categoryCd, categoryName))

      for (const item of categoryItems) {
        const approvalData: Record<string, ApprovalDetail> = {}
        historyByApproval.forEach((plMap, approvalId) => {
          const detail = plMap.get(item.productLineCode)
          approvalData[approvalId] = {
            ogmPercent: detail?.netMarginPercentage ?? null,
            totalDiscountPercentage: detail?.estimatedDiscountPercentage ?? null,
          }
        })

        treeNodes.push(buildProductLineNode(item, psgCode, categoryCd, categoryName, approvalData))
      }
    })
  })

  return {
    items: treeNodes,
    totals: {
      netRevenue: totals?.totalNetRevenue ?? 0,
      totalDiscountPercent: totals?.totalDiscountPercentage ?? 0,
      ogmPercent: totals?.totalOGMPercentage ?? 0,
    },
  }
}
